import * as Sentry from "@sentry/react";

export type QueryDataSource =
  | "api-response"
  | "bootstrap-api"
  | "react-query-cache"
  | "server-bootstrap";

type InvalidArrayQueryDataOptions = {
  queryName: string;
  source: QueryDataSource;
  context?: Record<string, unknown>;
};

const reportedObjects = new WeakMap<object, Set<string>>();
const reportedPrimitives = new Set<string>();

function getPropertyType(value: object, property: string): string {
  try {
    return typeof (value as Record<string, unknown>)[property];
  } catch {
    return "unreadable";
  }
}

function getConstructorName(value: object): string | undefined {
  try {
    return value.constructor?.name;
  } catch {
    return undefined;
  }
}

function getNavigationType(): string | undefined {
  try {
    if (
      typeof performance === "undefined" ||
      typeof performance.getEntriesByType !== "function"
    ) {
      return undefined;
    }

    return (
      performance.getEntriesByType("navigation")[0] as
        | PerformanceNavigationTiming
        | undefined
    )?.type;
  } catch {
    return undefined;
  }
}

function shouldReport(
  value: unknown,
  { queryName, source }: InvalidArrayQueryDataOptions,
): boolean {
  const reportKey = `${queryName}:${source}`;

  if (
    (typeof value === "object" && value !== null) ||
    typeof value === "function"
  ) {
    const reportsForValue = reportedObjects.get(value);
    if (reportsForValue?.has(reportKey)) return false;

    if (reportsForValue) {
      reportsForValue.add(reportKey);
    } else {
      reportedObjects.set(value, new Set([reportKey]));
    }

    return true;
  }

  const primitiveKey = `${reportKey}:${value === null ? "null" : typeof value}`;
  if (reportedPrimitives.has(primitiveKey)) return false;

  reportedPrimitives.add(primitiveKey);
  return true;
}

export function isUsableArrayQueryData<T>(value: unknown): value is T[] {
  if (!Array.isArray(value)) return false;

  try {
    return typeof value.find === "function";
  } catch {
    return false;
  }
}

export function reportInvalidArrayQueryData(
  value: unknown,
  options: InvalidArrayQueryDataOptions,
): TypeError {
  const error = new TypeError(
    `Invalid array data for query "${options.queryName}" from ${options.source}`,
  );

  if (!shouldReport(value, options)) return error;

  const isObject =
    (typeof value === "object" && value !== null) ||
    typeof value === "function";

  Sentry.captureException(error, {
    fingerprint: [
      "invalid-array-query-data",
      options.queryName,
      options.source,
    ],
    tags: {
      "query_data.is_array": String(Array.isArray(value)),
      "query_data.name": options.queryName,
      "query_data.source": options.source,
    },
    extra: {
      ...options.context,
      arrayLength: Array.isArray(value) ? value.length : undefined,
      constructorName: isObject ? getConstructorName(value) : undefined,
      findType: isObject ? getPropertyType(value, "find") : undefined,
      navigationType: getNavigationType(),
      valueType: value === null ? "null" : typeof value,
      wasDiscarded:
        typeof document === "undefined"
          ? undefined
          : (document as Document & { wasDiscarded?: boolean }).wasDiscarded,
    },
  });

  return error;
}

export function requireArrayQueryData<T>(
  value: unknown,
  options: InvalidArrayQueryDataOptions,
): T[] {
  if (isUsableArrayQueryData<T>(value)) return value;

  throw reportInvalidArrayQueryData(value, options);
}

export function arrayQueryDataOrFallback<T>(
  value: unknown,
  options: InvalidArrayQueryDataOptions,
): T[] {
  if (isUsableArrayQueryData<T>(value)) return value;

  reportInvalidArrayQueryData(value, options);
  return [];
}
